/**
 * Onboarding Server Functions
 *
 * This module provides TanStack Start server functions for onboarding-related
 * API calls. These functions wrap Orval-generated API functions to provide
 * server-side execution with automatic cookie forwarding via the custom instance.
 */

import { createServerFn } from "@tanstack/react-start";
import { getWebRequest } from "@tanstack/react-start/server";
import type {
  InvitationRedeemRequest,
  OnboardingStatusResponse,
  OnboardingWizardCompanyRequest,
  OnboardingWizardPersonRequest,
  OnboardingWizardResponse,
  WaitlistRequest,
  WorkspaceCreateRequest,
  WorkspaceResponse,
  ReadWaitlistApiOnboardingWaitlistGet200,
} from "@/lib/api/generated";
import {
  readOnboardingStatusApiOnboardingStatusGet,
  submitWaitlistEntryApiOnboardingWaitlistPost,
  updateWaitlistEntryApiOnboardingWaitlistPatch,
  readWaitlistApiOnboardingWaitlistGet,
  readOnboardingWizardApiOnboardingWizardGet,
  saveOnboardingWizardCompanyApiOnboardingWizardCompanyPut,
  completeOnboardingWizardPersonApiOnboardingWizardPersonPost,
} from "@/lib/api/generated/onboarding/onboarding";
import { createWorkspaceApiWorkspacesPost } from "@/lib/api/generated/workspaces/workspaces";
import { logInfo } from "@/lib/structured-log";
import { customInstance } from "@/lib/api/custom-instance";
import { resolveCountryFromGeoHeaders } from "@/lib/geo-country";

/**
 * Get onboarding status
 *
 * Server function that retrieves the current user's onboarding status.
 * Requires authentication.
 */
export const getOnboardingStatusServer = createServerFn({ method: "GET" }).handler(async () => {
  logInfo("onboarding.get_status.server_fn_called", {});

  const response = await readOnboardingStatusApiOnboardingStatusGet();

  if (response.status !== 200) {
    const errorBody =
      typeof response.data === "string" ? response.data : JSON.stringify(response.data);
    throw new Error(
      JSON.stringify({
        detail: "Failed to get onboarding status",
        status: response.status,
        body: errorBody,
      }),
    );
  }

  return response.data as OnboardingStatusResponse;
});

export const getOnboardingWizardServer = createServerFn({ method: "GET" }).handler(async () => {
  logInfo("onboarding.get_wizard.server_fn_called", {});
  const response = await readOnboardingWizardApiOnboardingWizardGet();
  if (response.status !== 200) {
    throw new Error(JSON.stringify(response.data));
  }
  const inferredCountry = resolveCountryFromGeoHeaders(getWebRequest().headers);
  return {
    ...(response.data as OnboardingWizardResponse),
    inferred_country: inferredCountry,
  };
});

export const saveOnboardingWizardCompanyServer = createServerFn({ method: "POST" })
  .validator((data: OnboardingWizardCompanyRequest) => data)
  .handler(async ({ data }) => {
    logInfo("onboarding.save_wizard_company.server_fn_called", {
      has_company_website: Boolean(data.company_website),
    });
    const response = await saveOnboardingWizardCompanyApiOnboardingWizardCompanyPut(data);
    if (response.status !== 200) {
      throw new Error(JSON.stringify(response.data));
    }
    return response.data as OnboardingWizardResponse;
  });

export const completeOnboardingWizardPersonServer = createServerFn({ method: "POST" })
  .validator((data: OnboardingWizardPersonRequest) => data)
  .handler(async ({ data }) => {
    logInfo("onboarding.complete_wizard_person.server_fn_called", {});
    const response = await completeOnboardingWizardPersonApiOnboardingWizardPersonPost(data);
    if (response.status !== 200) {
      throw new Error(JSON.stringify(response.data));
    }
    return response.data as OnboardingWizardResponse;
  });

/**
 * Redeem invitation code
 *
 * Server function that redeems an invitation code and returns updated
 * onboarding status. Requires authentication.
 */
export const redeemInvitationServer = createServerFn({ method: "POST" })
  .validator((data: InvitationRedeemRequest) => data)
  .handler(async ({ data }) => {
    logInfo("onboarding.redeem_invitation.server_fn_called", {
      code_last4: data.code.slice(-4),
    });

    const url = "/api/onboarding/invitations/redemptions";
    const response = await customInstance<{ data: unknown; status: number; headers: Headers }>(
      url,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    );

    if (response.status !== 200) {
      throw new Error(JSON.stringify(response.data));
    }

    const envelope = response.data as { data?: OnboardingStatusResponse };
    return (envelope?.data ??
      (response.data as OnboardingStatusResponse)) as OnboardingStatusResponse;
  });

/**
 * Submit waitlist entry
 *
 * Server function that submits a waitlist entry and returns updated
 * onboarding status. Requires authentication.
 */
export const submitWaitlistServer = createServerFn({ method: "POST" })
  .validator((data: WaitlistRequest) => data)
  .handler(async ({ data }) => {
    logInfo("onboarding.submit_waitlist.server_fn_called", {});

    const response = await submitWaitlistEntryApiOnboardingWaitlistPost(data);

    if (response.status !== 201) {
      throw new Error(JSON.stringify(response.data));
    }

    return response.data as OnboardingStatusResponse;
  });

/**
 * Update waitlist entry
 *
 * Server function that updates an existing waitlist entry.
 * Requires authentication.
 */
export const updateWaitlistServer = createServerFn({ method: "POST" })
  .validator((data: WaitlistRequest) => data)
  .handler(async ({ data }) => {
    logInfo("onboarding.update_waitlist.server_fn_called", {});

    const response = await updateWaitlistEntryApiOnboardingWaitlistPatch(data);

    if (response.status !== 200) {
      throw new Error(JSON.stringify(response.data));
    }

    return response.data as OnboardingStatusResponse;
  });

/**
 * Get waitlist entry
 *
 * Server function that retrieves the current user's waitlist entry.
 * Requires authentication.
 */
export const getWaitlistServer = createServerFn({ method: "GET" }).handler(async () => {
  logInfo("onboarding.get_waitlist.server_fn_called", {});

  const response = await readWaitlistApiOnboardingWaitlistGet();

  if (response.status !== 200) {
    throw new Error(
      JSON.stringify({
        detail: "Failed to get waitlist entry",
        status: response.status,
      }),
    );
  }

  return response.data as ReadWaitlistApiOnboardingWaitlistGet200;
});

/**
 * Create workspace
 *
 * Server function that creates a new workspace for the current user.
 * Requires authentication.
 */
export const createWorkspaceServer = createServerFn({ method: "POST" })
  .validator((data: WorkspaceCreateRequest) => data)
  .handler(async ({ data }) => {
    logInfo("onboarding.create_workspace.server_fn_called", {
      workspace_slug: data.slug,
    });

    const response = await createWorkspaceApiWorkspacesPost(data);

    if (response.status !== 201) {
      throw new Error(JSON.stringify(response.data));
    }

    return response.data as WorkspaceResponse;
  });

// ====== Profile Collection ======

interface UserProfileRequest {
  name?: string;
  phone_number: string;
  company_name?: string;
  job_title?: string;
  company_size?: string;
  company_website?: string;
  linkedin_url?: string;
  whatsapp?: string;
  location?: string;
  referral_source?: string;
}

interface UserProfileResponse {
  name?: string | null;
  phone_number: string;
  company_name?: string | null;
  job_title?: string | null;
  company_size?: string | null;
  company_website?: string | null;
  linkedin_url?: string | null;
  whatsapp?: string | null;
  location?: string | null;
  referral_source?: string | null;
}

/**
 * Save user profile (phone, company, referral source, etc.)
 *
 * Server function that creates or updates profile fields on the user.
 * Returns updated onboarding status.
 */
export const saveUserProfileServer = createServerFn({ method: "POST" })
  .validator((data: UserProfileRequest) => data)
  .handler(async ({ data }) => {
    logInfo("onboarding.save_profile.server_fn_called", {
      has_phone: !!data.phone_number,
      has_company_name: !!data.company_name,
      has_job_title: !!data.job_title,
      has_company_size: !!data.company_size,
    });

    const response = await customInstance<{ data: unknown; status: number }>(
      "/api/onboarding/profile",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    );

    if (response.status !== 200) {
      throw new Error(JSON.stringify(response.data));
    }

    return response.data as OnboardingStatusResponse;
  });

/**
 * Get user profile data
 *
 * Server function that retrieves the current user's profile fields.
 */
export const getUserProfileOnboardingServer = createServerFn({ method: "GET" }).handler(
  async () => {
    logInfo("onboarding.get_profile.server_fn_called", {});

    const response = await customInstance<{ data: unknown; status: number }>(
      "/api/onboarding/profile",
      { method: "GET" },
    );

    if (response.status !== 200) {
      return null;
    }

    return response.data as UserProfileResponse | null;
  },
);
